import React, { useState, useEffect, useRef } from "react";
import CustomDataGrid from "../components/CustomDataGrid";
import EntityDetails from "../components/EntityDetails";
import { Button, Alert, Spinner } from "react-bootstrap";
import JSZip from "jszip";
import QRCode from "qrcode";
import { saveAs } from "file-saver";
import {
  FaTrashAlt,
  FaCheckCircle,
  FaCloudUploadAlt,
  FaDownload,
} from "react-icons/fa";
import {
  campaignGridColumns,
  campaignMapping,
  campaignRowMapping,
  generateFormData,
  generateGridColumns,
  generateGridRows,
} from "../utils/PageUtils";
import { fetchCampaignData, uploadContactsToAPI } from "../utils/api";
import { useAuth } from "react-oidc-context";
import "../CustomStyle.css";
import PageHelmet from "../components/PageHelmet";
import { Modal, Box, Typography } from "@mui/material";
import { useSearchParams } from 'react-router-dom' 


const UploadContacts = ({ userRole, bearerToken }) => {
  const auth = useAuth();
  const fileInputRef = useRef(null);

  const [campaignsData, setCampaignsData] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [uploadError, setUploadError] = useState("");
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [zipFile, setZipFile] = useState(null);
  const [isUploaded, setIsUploaded] = useState(false);
  const [refreshGrid, setRefreshGrid] = useState(true);
  const [open, setOpen] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(null);

  const [searchParams] = useSearchParams()
  const testMode = searchParams.get('test-mode')

  console.log('Test Mode:', testMode)

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "auto",
    bgcolor: "background.paper",
    borderRadius: 2,
    boxShadow: 24,
    p: 4,
  };

  useEffect(() => {
    const loadCampaigns = async () => {
      if (!bearerToken) {
        return;
      }
      setIsLoading(true);
      try {
        const data = await fetchCampaignData(bearerToken);
        if (data?.records) {
          setCampaignsData(data.records);
        }
      } catch (error) {
        setError("An error occurred while fetching campaigns.");
      } finally {
        setIsLoading(false);
        if (refreshGrid) {
          setRefreshGrid(false);
        }
      }
    };
    if (refreshGrid) {
      loadCampaigns();
    }
  }, [refreshGrid]);

  const handleRowClick = (e) => {
    if (zipFile) {
      handleOpen();
      return;
    }
    setSelectedCampaign(null);
    resetUploadState();

    const selected = campaignsData[e.id - 1];
    setTimeout(() => {
      setSelectedCampaign(selected);
    }, 0);
    setSelectedRowId(e.id);
  };

  const handleFileChange = (event) => {
    const uploadedFile = event.target.files[0];
    resetMessages();

    if (!uploadedFile) return;
    if (uploadedFile.type !== "text/csv") {
      setUploadError("Only CSV files are allowed.");
      return;
    } else if (uploadedFile.size > 2 * 1024 * 1024) {
      setUploadError("File size should not exceed 2 MB.");
      return;
    } else {
      setFile(uploadedFile);
    }
  };

  const resetMessages = () => {
    setUploadError("");
    setInfo(null);
    if (!isUploaded) {
      setZipFile(null);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const resetUploadState = () => {
    setRows([]);
    setColumns([]);
    setFile(null);
    setZipFile(null);
    setInfo(null);
    setError(null);
    setIsUploaded(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const handleUpload = async () => {
    if (!file || !selectedCampaign) return;
    setIsUploaded(true);
    const payload = {
      customer_id: selectedCampaign.customer_id,
      campaign: selectedCampaign.campaign_name,
      destination_url: selectedCampaign.destination_url,
    };

    const result = await uploadContactsToAPI(file, payload, bearerToken);
    if (result.success) {
      const data = result.data;

      if (Array.isArray(data)) {
        const formattedRows = data.map((item) => ({
          ...item,
          id: item.ID,
        }));

        const formattedColumns = Object.keys(data[0]).map((key) => ({
          field: key,
          headerName: key.charAt(0).toUpperCase() + key.slice(1),
          width: 150,
          hideable: key !== "envelope_name"
        }));

        setRows(formattedRows);
        setColumns(formattedColumns);
        setInfo(result.message);
        generateQRCodesAndZip(data);
      }
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
      setRefreshGrid(true);
    } else {
      setError(result.message);
    }
  };

  const generateQRCodesAndZip = async (data) => {
    const zip = new JSZip();
    const qrZip = new JSZip();

    for (const row of data) {
      const url = row["o.rp.trackableurl"];
      const name = row["o.rp.qrcode_img"];

      if (url !== "Error" && name) {
        try {
          const qrDataUrl = await QRCode.toDataURL(url, {
            scale: 10,
            margin: 1,
          });
          const qrBlob = await (await fetch(qrDataUrl)).blob();
          qrZip.file(`${name}.png`, qrBlob);
        } catch (err) {
          setError("An error occurred while generating QR codes.");
        }
      }
    }
    const csvString = generateCsvString(data);
    zip.file(
      "processed_contacts.csv",
      new Blob([csvString], { type: "text/csv" })
    );
    zip.file("qrcodes.zip", await qrZip.generateAsync({ type: "blob" }));

    const finalZip = await zip.generateAsync({ type: "blob" });
    const timestamp = new Date().toISOString().replace(/[-:.]/g, "_");
    const name = selectedCampaign?.campaign_name || "unknown";

    setZipFile({
      blob: finalZip,
      name: `campaign_${name}_upload_${timestamp}.zip`,
    });
  };

  const generateCsvString = (data) => {
    const headers = Object.keys(data[0]).join(",");
    const body = data.map((row) => Object.values(row).join(",")).join("\n");
    return `${headers}\n${body}`;
  };

  const campaignFormData = selectedCampaign
    ? generateFormData(selectedCampaign, campaignMapping)
    : null;
  const campaignColumns = generateGridColumns(campaignGridColumns,"campaign_name");
  const campaignRows = generateGridRows(campaignsData, campaignRowMapping);

  const handleDownloadAndReload = () => {
    saveAs(zipFile.blob, zipFile.name);
    setZipFile(null);
    setIsUploaded(false);
    setRows([]);
    setColumns([]);
    setFile(null);
    setSelectedCampaign(null);
  };
  if (campaignsData.length < 0) {
    return (
      <div
        className="upload-contacts-container"
        style={{
          backgroundColor: "#ff9999 ",
          padding: "10px",
          borderRadius: "10px",
          color: "#ffffff",
        }}
      >
        <h2>No Existing campaigns found for this user </h2>
      </div>
    );
  }
  const handleClearAndRetry = () => {
    setError(null); 
    setUploadError(""); 
    setInfo(null); 
    setIsUploaded(false); 
    setFile(null); 
    if (fileInputRef.current) {
      fileInputRef.current.value = null; 
    }
    setRows([]);
    setColumns([]);
    setZipFile(null);
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Please wait...</span>
        </Spinner>
      </div>
    );
  }
  return userRole === "customer" ? (
    <>
      <div className="upload-contacts-container">
        <PageHelmet
          title="Upload Contacts"
          description="Upload and process your contacts with TraQR."
          url={`${process.env.REACT_APP_REDIRECT_URI}/upload_contacts`}
        />
        <h1 className="main-heading">TraQR - Upload Contacts</h1>
        {campaignsData.length === 0 ? (
          <div className="no-campaigns">
            <Alert variant="danger">
              No existing campaigns were found for this user.
            </Alert>
          </div>
        ) : (
          <>
            <h4 className="subheading-1">
              Select a campaign to upload contacts
            </h4>
            <p className="upload-para">
              Mandatory CSV columns: ID, envelope_name, greeting_name,
              ma-addr_line1, ma-city, ma-state, ma-zip.
            </p>
            <div className="create-main-container ">
              <div
              className={`create-grid-form ${
                selectedCampaign ? "grid-to-left" : "grid-centered"
              } `}
              >
              <div className="create-grid-container">
                <CustomDataGrid
                  rows={campaignRows}
                  columns={campaignColumns}
                  totalRows={campaignsData.length}
                  onRowClick={handleRowClick}
                  selectedRowId={selectedRowId}
                />
              </div>
              {selectedCampaign && (

              <div className="create-form-container upload-contacts-right">
                
                    <EntityDetails inputs={campaignFormData} width={"100px"} />
              </div>
              )}
              </div>
             {selectedCampaign && (

              <div className="upload-contacts-center">
                    <div className="file-upload-container">
                      <p className="upload-heading">{`Upload CSV file for campaign '${selectedCampaign.campaign_name}'`}</p>
                      <div className="upload-area">
                        <label htmlFor="fileInput" className="upload-label">
                          <div className="upload-box">
                            <FaCloudUploadAlt size={20} color="black" />
                            <span className="upload-browse-span">Browse</span>
                          </div>
                          <span className="upload-drag-span">
                            or drag and drop
                          </span>
                        </label>
                        <input
                          type="file"
                          id="fileInput"
                          accept=".csv"
                          onChange={handleFileChange}
                          className="file-input"
                          ref={fileInputRef}
                        />
                      </div>

                      {uploadError && (
                        <p className="error-message">{uploadError}</p>
                      )}

                      {file && (
                        <div className="file-list">
                          <div className="file-item">
                            <span>{file.name}</span>
                            <FaCheckCircle className="icon-success" />
                            <FaTrashAlt
                              className="icon-delete"
                              onClick={handleRemoveFile}
                            />
                          </div>
                        </div>
                      )}

                      <Button
                        variant="dark"
                        className="upload-button"
                        onClick={handleUpload}
                        disabled={!file || isUploaded}
                      >
                        Upload
                      </Button>
                    </div>
                    {error && (
                      <>
                        <Alert data-testid="error-alert" variant="danger">
                          {error}
                        </Alert>
                        <Button
                          variant="danger"
                          className="upload-button"
                          onClick={handleClearAndRetry}
                        >
                          Clear message and retry
                        </Button>
                      </>
                    )}

                    {info && (
                      <Alert
                        data-testid="success-alert"
                        variant="success"
                        className="info-alert"
                      >
                        {info}
                      </Alert>
                    )}

                    {zipFile && testMode && (
                      <Button
                        variant="outline-success"
                        className="upload-button download-button"
                        onClick={handleDownloadAndReload}
                      >
                        <FaDownload size={20} style={{ marginRight: "8px" }} />
                        Download CSV and QR Codes
                      </Button>
                    )}
              </div>   
             )}
            </div>
          </>
        )}
      </div>

      <Modal
        open={open}
        onClose={(event, reason) => {
          if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
            handleClose();
          }
        }}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
        disableEscapeKeyDown
      >
        <Box sx={style}>
          <Typography
            id="modal-description"
            className="modal-title"
            sx={{
              mb: 3,
              textAlign: "left",
              "@media (max-width:768px)": {
                fontSize: "2.4vw",
              },
            }}
          >
            Please download the processed CSV and QR codes before proceeding.
          </Typography>
          <Box sx={{ textAlign: "center" }}>
            <Button
              variant="danger"
              onClick={handleClose}
              className="ok-button"
            >
              OK
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  ) : (
    <div className="user-container">
      <Alert variant="danger">You do not have access to this page.</Alert>
    </div>
  );
};

export default UploadContacts;
